import { forwardRef } from 'react';
import { formatDate } from '@/utils/dateFormat';

// Helper function to convert number to words (Indian format)
const numberToWords = (num) => {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  
  if (num === 0) return 'Zero';
  
  const convertLessThanThousand = (n) => {
    if (n === 0) return '';
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + convertLessThanThousand(n % 100) : '');
  };
  
  const convert = (n) => {
    if (n < 1000) return convertLessThanThousand(n);
    if (n < 100000) return convertLessThanThousand(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + convertLessThanThousand(n % 1000) : '');
    if (n < 10000000) return convertLessThanThousand(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + convert(n % 100000) : '');
    return convertLessThanThousand(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + convert(n % 10000000) : '');
  };
  
  const rupees = Math.floor(num);
  const paise = Math.round((num - rupees) * 100);
  
  let result = 'Rupees ' + convert(rupees);
  if (paise > 0) {
    result += ' and ' + convert(paise) + ' Paise';
  }
  result += ' Only';
  
  return result;
};

// Format currency with 2 decimal places
const formatCurrency = (amount) => {
  return (amount || 0).toLocaleString('en-IN', { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  });
};

// Default logo URL
const DEFAULT_LOGO = 'https://d3ki85qs1zca4t.cloudfront.net/hq/brd-20250118-075016-HQ001-aaef9b/logo.png';

const A4GSTInvoice = forwardRef(({ sale, storeInfo, gstSettings }, ref) => {
  if (!sale) return null;

  const totalQty = sale.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
  
  // Calculate tax summary by tax rate
  const taxSummary = {};
  let totalTaxableValue = 0;
  let totalCGST = 0;
  let totalSGST = 0;
  let totalIGST = 0;
  
  sale.items?.forEach(item => {
    const rate = item.tax_rate || 0;
    const groupName = item.tax_group_name || (rate > 0 ? `GST ${rate}%` : 'Exempt');
    
    if (!taxSummary[groupName]) {
      taxSummary[groupName] = { 
        rate, 
        taxable: 0, 
        cgst: 0, 
        sgst: 0, 
        igst: 0, 
        total: 0,
        cgstRate: item.cgst_rate || rate / 2,
        sgstRate: item.sgst_rate || rate / 2
      };
    }
    taxSummary[groupName].taxable += item.taxable_amount || 0;
    taxSummary[groupName].cgst += item.cgst_amount || 0;
    taxSummary[groupName].sgst += item.sgst_amount || 0;
    taxSummary[groupName].igst += item.igst_amount || 0;
    taxSummary[groupName].total += item.tax_amount || 0;
    
    totalTaxableValue += item.taxable_amount || 0;
    totalCGST += item.cgst_amount || 0;
    totalSGST += item.sgst_amount || 0;
    totalIGST += item.igst_amount || 0;
  });

  const hasIGST = sale.total_igst > 0 || totalIGST > 0;
  const billDiscountAmount = sale.bill_discount?.amount || 0;

  // Calculate MRP Total and Total Savings
  let mrpTotal = 0;
  let totalSavings = 0;
  sale.items?.forEach(item => {
    const mrp = item.mrp || item.unit_price;
    const qty = item.quantity || 1;
    mrpTotal += mrp * qty;
    const itemSaving = (mrp - item.unit_price) * qty;
    if (itemSaving > 0) {
      totalSavings += itemSaving;
    }
  });
  // Add bill discount to savings
  totalSavings += billDiscountAmount;

  // Store address formatting
  const formatAddress = (addr) => {
    if (!addr) return '';
    if (typeof addr === 'string') return addr;
    const parts = [];
    if (addr.address_line1) parts.push(addr.address_line1);
    if (addr.address_line2) parts.push(addr.address_line2);
    if (addr.area) parts.push(addr.area);
    if (addr.district || addr.city) parts.push(addr.district || addr.city);
    if (addr.state) parts.push(addr.state);
    if (addr.pincode) parts.push('- ' + addr.pincode);
    return parts.join(', ');
  };

  // Format customer full address
  const formatCustomerAddress = () => {
    const parts = [];
    if (sale.customer_address) parts.push(sale.customer_address);
    if (sale.customer_district) parts.push(sale.customer_district);
    if (sale.customer_city) parts.push(sale.customer_city);
    if (sale.customer_state) parts.push(sale.customer_state);
    if (sale.customer_pincode) parts.push('- ' + sale.customer_pincode);
    return parts.join(', ') || '';
  };

  // Get GST settings - ONLY from gstSettings (store GST bill settings from HQ)
  const settings = gstSettings || {};
  const bankDetails = settings.bank_details || {};
  const invoiceSettings = settings.invoice_settings || {};
  
  // Get logo URL
  const logoUrl = storeInfo?.logo_url || DEFAULT_LOGO;

  // Customer address
  const customerFullAddress = formatCustomerAddress();
  // Shipping address - same as billing if not provided separately
  const shippingAddress = sale.shipping_address || customerFullAddress;

  return (
    <div ref={ref} className="a4-invoice" data-testid="a4-gst-invoice">
      {/* Invoice Header with Logo */}
      <div className="invoice-header">
        <div className="logo-section">
          <img src={logoUrl} alt="Company Logo" className="company-logo" onError={(e) => { e.target.style.display = 'none'; }} />
          <h2 className="store-name">{storeInfo?.name || 'SOON YOUNG'}</h2>
        </div>
        <div className="header-title">
          <h1 className="invoice-title">TAX INVOICE</h1>
          <p className="invoice-subtitle">(Original for Recipient)</p>
        </div>
      </div>

      {/* Company & Customer Details */}
      <div className="details-section">
        <div className="seller-details">
          <h3 className="section-title">Seller Details</h3>
          <p className="address">{formatAddress(storeInfo?.address)}</p>
          <p><span className="label">GSTIN:</span> {sale.store_gstin || storeInfo?.gstin}</p>
          {storeInfo?.pan && <p><span className="label">PAN:</span> {storeInfo.pan}</p>}
          {storeInfo?.contact?.phone && <p><span className="label">Phone:</span> {storeInfo.contact.phone}</p>}
          {storeInfo?.contact?.email && <p><span className="label">Email:</span> {storeInfo.contact.email}</p>}
        </div>
        
        <div className="buyer-details">
          <h3 className="section-title">Bill To</h3>
          <p className="customer-name">{sale.customer_name || 'Walk-in Customer'}</p>
          {customerFullAddress && <p className="address">{customerFullAddress}</p>}
          {sale.customer_state && <p><span className="label">State:</span> {sale.customer_state}</p>}
          {sale.customer_gstin && <p><span className="label">GSTIN:</span> {sale.customer_gstin}</p>}
          {sale.customer_phone && <p><span className="label">Mobile:</span> {sale.customer_phone}</p>}
        </div>
      </div>

      {/* Ship To - if different from Bill To */}
      {invoiceSettings.show_shipping_address && shippingAddress && (
        <div className="shipping-section">
          <h3 className="section-title">Ship To</h3>
          <p className="customer-name">{sale.customer_name || 'Walk-in Customer'}</p>
          <p className="address">{shippingAddress}</p>
        </div>
      )}

      {/* Invoice Details */}
      <div className="invoice-meta">
        <div className="meta-row">
          <div><span className="label">Invoice No:</span> {sale.bill_number}</div>
          <div><span className="label">Invoice Date:</span> {formatDate(sale.bill_datetime)}</div>
        </div>
        <div className="meta-row">
          <div><span className="label">Place of Supply:</span> {sale.customer_state || storeInfo?.address?.state || 'Tamil Nadu'}</div>
          <div><span className="label">Cashier:</span> {sale.cashier_name}</div>
        </div>
      </div>

      {/* Items Table - with HSN/MRP/GST/Discount on second line */}
      <table className="items-table">
        <thead>
          <tr>
            <th className="th-sno">S.No</th>
            <th className="th-item">Item Description</th>
            <th className="th-qty">Qty</th>
            <th className="th-rate">Rate</th>
            <th className="th-taxable">Basic</th>
            {hasIGST ? (
              <th className="th-tax">IGST</th>
            ) : (
              <>
                <th className="th-tax">CGST</th>
                <th className="th-tax">SGST</th>
              </>
            )}
            <th className="th-total">Total</th>
          </tr>
        </thead>
        <tbody>
          {sale.items?.map((item, idx) => {
            const mrp = item.mrp || item.unit_price;
            const discountPercent = mrp > 0 ? ((mrp - item.unit_price) / mrp * 100) : 0;
            const gstPercent = item.tax_rate || 0;
            const hsnCode = item.hsn_code ? String(item.hsn_code).padStart(6, '0') : '';
            
            return (
              <tr key={idx}>
                <td className="td-sno">{idx + 1}</td>
                <td className="td-item">
                  <div className="item-name">{item.product_name}</div>
                  <div className="item-details">
                    {hsnCode && <span className="detail-text">HSN: {hsnCode}</span>}
                    <span className="detail-text">MRP: ₹{formatCurrency(mrp)}</span>
                    <span className="detail-text">GST: {gstPercent}%</span>
                    {discountPercent > 0 && <span className="detail-text">Disc: {discountPercent.toFixed(1)}%</span>}
                  </div>
                  {(item.applied_offer_name || item.applied_offer_code || item.offer_name || item.offer_code) && (
                    <div className="item-offer-line">
                      🎁 Offer: <strong>{item.applied_offer_name || item.offer_name || item.applied_offer_code || item.offer_code}</strong>
                      {item.applied_offer?.offer_type === 'percentage_discount' && item.applied_offer?.discount_value
                        ? ` (${item.applied_offer.discount_value}% off)` : ''}
                      {item.discount_amount > 0 && ` · -₹${Number(item.discount_amount).toFixed(2)}`}
                    </div>
                  )}
                </td>
                <td className="td-qty">{item.quantity} {item.uom || 'PCS'}</td>
                <td className="td-rate">{formatCurrency(item.unit_price)}</td>
                <td className="td-taxable">{formatCurrency(item.taxable_amount)}</td>
                {hasIGST ? (
                  <td className="td-tax">{formatCurrency(item.igst_amount || item.tax_amount)}</td>
                ) : (
                  <>
                    <td className="td-tax">{formatCurrency(item.cgst_amount)}</td>
                    <td className="td-tax">{formatCurrency(item.sgst_amount)}</td>
                  </>
                )}
                <td className="td-total">{formatCurrency(item.total_amount)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* GST Summary & Totals Section - Side by Side */}
      <div className="summary-totals-section">
        {/* GST Summary Table - Left Side */}
        <div className="gst-summary-wrapper">
          <h4>GST Summary</h4>
          <table className="gst-table">
            <thead>
              <tr>
                <th>Tax Group</th>
                <th>Taxable Value</th>
                {hasIGST ? (
                  <>
                    <th>IGST %</th>
                    <th>IGST Amount</th>
                  </>
                ) : (
                  <>
                    <th>CGST %</th>
                    <th>CGST Amount</th>
                    <th>SGST %</th>
                    <th>SGST Amount</th>
                  </>
                )}
                <th>Total Tax</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(taxSummary).map(([key, data]) => (
                <tr key={key}>
                  <td className="td-left">{key}</td>
                  <td>{formatCurrency(data.taxable)}</td>
                  {hasIGST ? (
                    <>
                      <td>{data.rate}%</td>
                      <td>{formatCurrency(data.igst || data.total)}</td>
                    </>
                  ) : (
                    <>
                      <td>{data.cgstRate}%</td>
                      <td>{formatCurrency(data.cgst)}</td>
                      <td>{data.sgstRate}%</td>
                      <td>{formatCurrency(data.sgst)}</td>
                    </>
                  )}
                  <td>{formatCurrency(data.total)}</td>
                </tr>
              ))}
              <tr className="total-row">
                <td className="td-left"><strong>Total</strong></td>
                <td><strong>{formatCurrency(totalTaxableValue)}</strong></td>
                {hasIGST ? (
                  <>
                    <td>-</td>
                    <td><strong>{formatCurrency(totalIGST)}</strong></td>
                  </>
                ) : (
                  <>
                    <td>-</td>
                    <td><strong>{formatCurrency(totalCGST)}</strong></td>
                    <td>-</td>
                    <td><strong>{formatCurrency(totalSGST)}</strong></td>
                  </>
                )}
                <td><strong>{formatCurrency(totalCGST + totalSGST + totalIGST)}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Totals + Payment - Right Side */}
        <div className="totals-wrapper">
          <h4>Invoice Summary</h4>
          <table className="totals-table">
            <tbody>
              <tr>
                <td>Total Qty:</td>
                <td>{totalQty}</td>
              </tr>
              <tr>
                <td>MRP Total:</td>
                <td>₹{formatCurrency(mrpTotal)}</td>
              </tr>
              {totalSavings > 0 && (
                <tr className="discount-row">
                  <td>Total Discount:</td>
                  <td>-₹{formatCurrency(totalSavings)}</td>
                </tr>
              )}
              <tr>
                <td>Sub Total:</td>
                <td>₹{formatCurrency(sale.subtotal)}</td>
              </tr>
              {billDiscountAmount > 0 && (
                <tr>
                  <td>Bill Discount ({sale.bill_discount?.value}{sale.bill_discount?.type === 'percent' ? '%' : ''}):</td>
                  <td>-₹{formatCurrency(billDiscountAmount)}</td>
                </tr>
              )}
              <tr>
                <td>Basic Amount:</td>
                <td>₹{formatCurrency(totalTaxableValue)}</td>
              </tr>
              {hasIGST ? (
                <tr>
                  <td>IGST:</td>
                  <td>₹{formatCurrency(totalIGST || sale.total_igst)}</td>
                </tr>
              ) : (
                <>
                  <tr>
                    <td>CGST:</td>
                    <td>₹{formatCurrency(totalCGST || sale.total_cgst)}</td>
                  </tr>
                  <tr>
                    <td>SGST:</td>
                    <td>₹{formatCurrency(totalSGST || sale.total_sgst)}</td>
                  </tr>
                </>
              )}
              {sale.round_off !== 0 && (
                <tr>
                  <td>Round Off:</td>
                  <td>{sale.round_off > 0 ? '+' : ''}₹{sale.round_off?.toFixed(2)}</td>
                </tr>
              )}
              <tr className="grand-total">
                <td><strong>Grand Total:</strong></td>
                <td><strong>₹{formatCurrency(sale.grand_total)}</strong></td>
              </tr>
            </tbody>
          </table>
          
          {/* Payment Details - Under Grand Total */}
          <div className="payment-inline">
            <h5>Payment Details</h5>
            <div className="payment-list">
              {sale.payments?.map((pay, idx) => (
                <div key={idx} className="payment-item">
                  <span>{pay.mode.toUpperCase()}:</span>
                  <span>₹{formatCurrency(pay.amount)}</span>
                </div>
              ))}
              {/* Show Change Given if cash payment */}
              {sale.payments?.some(p => p.mode?.toLowerCase() === 'cash') && sale.change_amount > 0 && (
                <div className="payment-item change-given">
                  <span>Change Given:</span>
                  <span>₹{formatCurrency(sale.change_amount)}</span>
                </div>
              )}
              {sale.payments?.some(p => p.reference) && (
                <div className="payment-refs">
                  {sale.payments.filter(p => p.reference).map((pay, idx) => (
                    <span key={idx} className="ref-text">Ref: {pay.reference}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Amount in Words & Your Savings - Full Width */}
      <div className="amount-savings-section">
        <div className="amount-words">
          <span className="label">Amount in Words:</span>
          <p>{numberToWords(sale.grand_total)}</p>
        </div>
        {totalSavings > 0 && (
          <div className="savings-box">
            <span className="savings-label">🎉 Your Savings:</span>
            <span className="savings-amount">₹{formatCurrency(totalSavings)}</span>
          </div>
        )}
      </div>

      {/* Bank Details (Left) & Terms and Conditions (Right) - Side by Side */}
      <div className="bank-terms-section">
        {/* Bank Details - Left Side */}
        {invoiceSettings.show_bank_details !== false && bankDetails.bank_name && (
          <div className="bank-details">
            <h4>Bank Details for Payment</h4>
            <p><span className="label">Bank Name:</span> {bankDetails.bank_name}</p>
            <p><span className="label">Account No:</span> {bankDetails.account_number}</p>
            <p><span className="label">IFSC Code:</span> {bankDetails.ifsc_code}</p>
            {bankDetails.branch && <p><span className="label">Branch:</span> {bankDetails.branch}</p>}
            <p><span className="label">Account Type:</span> {bankDetails.account_type || 'Current'}</p>
          </div>
        )}
        
        {/* Terms & Conditions - Right Side */}
        {invoiceSettings.terms_and_conditions && (
          <div className="terms-section">
            <h4>Terms & Conditions</h4>
            <p className="terms-content">
              {invoiceSettings.terms_and_conditions}
            </p>
          </div>
        )}
      </div>

      {/* Footer Note - from GST Invoice Settings (HQ) */}
      {invoiceSettings.footer_note && (
        <div className="footer-note">
          <p>{invoiceSettings.footer_note}</p>
        </div>
      )}

      {/* Signature Section - from GST Invoice Settings (HQ) */}
      <div className="signature-section">
        <div className="customer-signature">
          <p>Customer Signature</p>
          <div className="signature-line"></div>
        </div>
        <div className="authorized-signature">
          <p className="company-name">{storeInfo?.name || 'SOON YOUNG'}</p>
          {invoiceSettings.signature_image_url && (
            <img 
              src={invoiceSettings.signature_image_url} 
              alt="Authorized Signature" 
              className="signature-image"
            />
          )}
          <p className="signatory-name">{invoiceSettings.signatory_name || 'Authorized Signatory'}</p>
          <div className="signature-line"></div>
        </div>
      </div>

      {/* Computer Generated Notice */}
      <div className="generated-notice">
        <p>This is a computer generated invoice and does not require a physical signature.</p>
      </div>

      {/* Styles for A4 GST Invoice - Print Optimized */}
      <style>{`
        .a4-invoice {
          width: 210mm;
          padding: 10mm;
          font-family: 'Arial', 'Helvetica', sans-serif;
          font-size: 10px;
          line-height: 1.5;
          background: white;
          color: #000;
          box-sizing: border-box;
          break-inside: avoid;
          page-break-inside: avoid;
        }
        
        /* Header with Logo */
        .invoice-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid #ccc;
          padding-bottom: 10px;
          margin-bottom: 12px;
        }
        .logo-section {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        }
        .company-logo {
          max-height: 50px;
          max-width: 120px;
          margin-bottom: 5px;
        }
        .store-name {
          font-size: 14px;
          font-weight: 700;
          margin: 0;
          color: #000;
        }
        .header-title {
          text-align: right;
        }
        .invoice-title {
          font-size: 20px;
          font-weight: 700;
          margin: 0;
          text-transform: uppercase;
          color: #000;
        }
        .invoice-subtitle {
          font-size: 10px;
          margin: 2px 0 0 0;
          color: #333;
        }
        
        /* Details Section */
        .details-section {
          display: flex;
          gap: 20px;
          margin-bottom: 12px;
          border: 1px solid #999;
        }
        .seller-details, .buyer-details {
          flex: 1;
          padding: 10px 12px;
        }
        .seller-details {
          border-right: 1px solid #999;
        }
        .section-title {
          font-size: 11px;
          font-weight: 700;
          margin: 0 0 6px 0;
          text-transform: uppercase;
          color: #000;
          border-bottom: 1px solid #ccc;
          padding-bottom: 4px;
        }
        .customer-name {
          font-size: 12px;
          font-weight: 700;
          margin-bottom: 4px;
          color: #000;
        }
        .address {
          font-size: 10px;
          margin-bottom: 4px;
          color: #000;
          line-height: 1.4;
        }
        .details-section p {
          margin: 3px 0;
          font-size: 10px;
          color: #000;
        }
        .label {
          font-weight: 700;
          color: #000;
        }
        
        /* Shipping Section */
        .shipping-section {
          border: 1px solid #999;
          padding: 10px 12px;
          margin-bottom: 12px;
        }
        
        /* Invoice Meta */
        .invoice-meta {
          border: 1px solid #999;
          padding: 8px 12px;
          margin-bottom: 12px;
          background: #f5f5f5;
        }
        .meta-row {
          display: flex;
          justify-content: space-between;
          font-size: 10px;
          padding: 3px 0;
          color: #000;
        }
        
        /* Items Table - Print Optimized */
        .items-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 12px;
          font-size: 9px;
        }
        .items-table th, .items-table td {
          border: 1px solid #999;
          padding: 8px 6px;
          text-align: center;
          color: #000;
        }
        .items-table th {
          background: #e5e5e5;
          font-weight: 700;
          font-size: 9px;
          text-transform: uppercase;
          color: #000;
        }
        .th-sno { width: 30px; }
        .th-item { text-align: left; min-width: 180px; }
        .th-qty { width: 60px; }
        .th-rate, .th-taxable, .th-total { width: 70px; }
        .th-tax { width: 55px; }
        
        .td-item { text-align: left; }
        .td-qty, .td-rate, .td-taxable, .td-total, .td-tax {
          text-align: right;
        }
        .item-name { 
          font-weight: 700; 
          font-size: 10px;
          color: #000;
          margin-bottom: 2px;
        }
        .item-details { 
          font-size: 9px; 
          color: #000;
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          margin-top: 2px;
        }
        /* No background for HSN/GST/Disc - darker font for print */
        .detail-text {
          font-weight: 600;
          color: #000;
        }
        .item-offer-line {
          margin-top: 3px;
          font-size: 10px;
          font-weight: 700;
          color: #000;
          padding: 2px 6px;
          border: 1px dashed #000;
          display: inline-block;
        }
        
        /* GST Summary */
        .gst-summary {
          margin-bottom: 12px;
        }
        .gst-summary h4 {
          font-size: 11px;
          margin: 0 0 6px 0;
          font-weight: 700;
          color: #000;
        }
        
        /* Summary & Totals Side by Side */
        .summary-totals-section {
          display: flex;
          gap: 15px;
          margin-bottom: 12px;
        }
        .gst-summary-wrapper {
          flex: 1;
        }
        .gst-summary-wrapper h4, .totals-wrapper h4 {
          font-size: 11px;
          margin: 0 0 6px 0;
          font-weight: 700;
          color: #000;
          border-bottom: 1px solid #999;
          padding-bottom: 4px;
        }
        .totals-wrapper {
          width: 200px;
          flex-shrink: 0;
        }
        
        .gst-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 9px;
        }
        .gst-table th, .gst-table td {
          border: 1px solid #999;
          padding: 5px 6px;
          text-align: right;
          color: #000;
        }
        .gst-table th {
          background: #e5e5e5;
          font-weight: 700;
          text-align: center;
          font-size: 8px;
          color: #000;
        }
        .gst-table .td-left {
          text-align: left;
        }
        .gst-table .total-row {
          background: #f0f0f0;
        }
        
        /* Totals Wrapper with Payment */
        .totals-wrapper {
          width: 220px;
          flex-shrink: 0;
        }
        .totals-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 10px;
        }
        .totals-table td {
          border: 1px solid #999;
          padding: 5px 8px;
          color: #000;
        }
        .totals-table td:first-child {
          text-align: left;
          color: #000;
        }
        .totals-table td:last-child {
          text-align: right;
          font-weight: 600;
        }
        .totals-table .grand-total td {
          background: #e5e5e5;
          font-size: 11px;
          font-weight: 700;
          color: #000;
        }
        .totals-table .discount-row td {
          color: #228B22;
          font-weight: 700;
        }
        
        /* Payment Inline (under grand total) */
        .payment-inline {
          border: 1px solid #999;
          border-top: none;
          padding: 8px 10px;
          background: #f9f9f9;
        }
        .payment-inline h5 {
          font-size: 9px;
          font-weight: 700;
          margin: 0 0 6px 0;
          color: #000;
          text-transform: uppercase;
        }
        .payment-list {
          font-size: 9px;
        }
        .payment-item {
          display: flex;
          justify-content: space-between;
          padding: 2px 0;
          color: #000;
        }
        .payment-item.change-given {
          border-top: 1px dashed #999;
          margin-top: 4px;
          padding-top: 4px;
          font-weight: 700;
          color: #228B22;
        }
        .payment-refs {
          margin-top: 4px;
          font-size: 8px;
          color: #666;
        }
        .ref-text {
          display: block;
        }
        
        /* Amount in Words & Savings Section */
        .amount-savings-section {
          margin-bottom: 12px;
        }
        .amount-words {
          border: 1px solid #999;
          padding: 8px 12px;
          margin-bottom: 8px;
        }
        .amount-words .label {
          font-size: 10px;
          color: #000;
        }
        .amount-words p {
          font-size: 11px;
          font-weight: 700;
          margin: 5px 0 0 0;
          text-transform: uppercase;
          color: #000;
        }
        /* Savings Box */
        .savings-box {
          border: 2px solid #228B22;
          background: #f0fff0;
          padding: 10px 12px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .savings-label {
          font-size: 11px;
          font-weight: 700;
          color: #228B22;
        }
        .savings-amount {
          font-size: 14px;
          font-weight: 700;
          color: #228B22;
        }
        
        /* Bank Details & Terms Section - Side by Side */
        .bank-terms-section {
          display: flex;
          gap: 15px;
          margin-bottom: 12px;
        }
        .bank-details {
          flex: 1;
          border: 1px solid #999;
          padding: 10px 12px;
        }
        .bank-details h4 {
          font-size: 10px;
          margin: 0 0 6px 0;
          font-weight: 700;
          border-bottom: 1px solid #ccc;
          padding-bottom: 4px;
          color: #000;
        }
        .bank-details p {
          margin: 3px 0;
          font-size: 9px;
          color: #000;
        }
        .terms-section {
          flex: 1;
          border: 1px solid #999;
          padding: 10px 12px;
        }
        .terms-section h4 {
          font-size: 10px;
          margin: 0 0 6px 0;
          font-weight: 700;
          border-bottom: 1px solid #ccc;
          padding-bottom: 4px;
          color: #000;
        }
        .terms-content {
          font-size: 9px;
          white-space: pre-wrap;
          margin: 0;
          color: #000;
          line-height: 1.5;
        }
        
        /* Footer Note */
        .footer-note {
          text-align: center;
          padding: 8px;
          margin-bottom: 12px;
        }
        .footer-note p {
          margin: 0;
          font-size: 10px;
          font-style: italic;
          color: #333;
        }
        
        /* Signature Section */
        .signature-section {
          display: flex;
          justify-content: space-between;
          margin-top: 30px;
          padding-top: 20px;
        }
        .customer-signature, .authorized-signature {
          width: 45%;
          text-align: center;
        }
        .signature-line {
          border-top: 1px solid #000;
          margin-top: 40px;
          width: 80%;
          margin-left: auto;
          margin-right: auto;
        }
        .signature-image {
          max-height: 40px;
          max-width: 100px;
          margin-bottom: 5px;
        }
        .signatory-name {
          font-size: 10px;
          margin-top: 5px;
          color: #333;
        }
        .authorized-signature .company-name {
          font-size: 11px;
          font-weight: 700;
          margin-bottom: 5px;
          color: #000;
        }
        
        /* Generated Notice */
        .generated-notice {
          text-align: center;
          margin-top: 15px;
          padding-top: 10px;
          border-top: 1px dashed #999;
        }
        .generated-notice p {
          font-size: 9px;
          color: #666;
          margin: 0;
          font-style: italic;
        }
        
        /* Print Styles */
        @media print {
          .a4-invoice {
            width: 210mm;
            padding: 8mm;
            margin: 0;
            box-shadow: none;
            break-inside: avoid;
            page-break-inside: avoid;
          }
          @page {
            size: A4;
            margin: 0;
          }
        }
      `}</style>
    </div>
  );
});

A4GSTInvoice.displayName = 'A4GSTInvoice';

export default A4GSTInvoice;
